import {
  type CharacterSummary,
  isGuestAccount,
  PASSWORD_MIN,
  SIGN_IN_PROVIDERS,
  type SignInProvider,
} from '@shefira/shared';

import { loadCharacterAtlas } from '../assets/dom-atlas.js';
import { deleteCharacter, listRoster, selectCharacter, upgradeAccount } from '../net/account.js';
import { AuthError, type StoredSession } from '../net/session.js';
import {
  leaveForProvider,
  PROVIDER_NAMES,
  signInProviders,
  takeLinkNotice,
} from '../net/sign-in.js';
import { confirmCharacterDeletion } from './delete-character-modal.js';
import { providerButton } from './provider-button.js';
import { requestHome } from './home.js';
import { characterCard } from './roster.js';
import { uiFormat, uiHtml, uiText } from './text.js';

/**
 * The profile window's account tab: the account's other characters, ready
 * to switch to; a way back to the home screen; and, while the account is
 * still a guest one, the form that turns it into a real account. The server
 * refuses a second upgrade, so a player who already has an email simply does
 * not see the form. Google and Steam link from here too (ADR 0054): the page
 * leaves for the provider and comes back to this tab.
 */
export class AccountTab {
  readonly element: HTMLElement;
  private loaded = false;
  private readonly error: HTMLElement;
  private readonly emailLine: HTMLElement;
  private readonly list: HTMLElement;
  private readonly form: HTMLFormElement;
  private readonly providers: HTMLElement;
  private readonly notice: HTMLElement;

  constructor(private readonly session: StoredSession) {
    this.element = document.createElement('div');
    this.element.className = 'profile__tab-body';
    this.element.innerHTML = uiHtml(`
      <p class="login__hint account-panel__email"></p>
      <ul class="roster account-panel__roster"></ul>
      <p class="login__hint">Clique un autre personnage pour l’incarner.</p>
      <form class="login__form account-panel__upgrade" novalidate hidden>
        <p class="login__hint">
          Ajoute un e-mail et un mot de passe pour retrouver tes personnages depuis n’importe quel
          navigateur. Rien n’est perdu : c’est le même compte.
        </p>
        <input class="login__input" name="email" type="email" autocomplete="email"
               placeholder="E-mail" aria-label="E-mail" />
        <input class="login__input" name="password" type="password" autocomplete="new-password"
               minlength="${String(PASSWORD_MIN)}" placeholder="Mot de passe" aria-label="Mot de passe" />
        <button class="login__submit" type="submit">Enregistrer le compte</button>
      </form>
      <div class="account-panel__link" hidden>
        <p class="login__hint">Ou retrouve ton compte avec :</p>
        <div class="account-panel__providers"></div>
      </div>
      <p class="login__hint account-panel__notice" role="status" hidden></p>
      <p class="login__error" role="alert" hidden></p>
      <div class="account-panel__actions">
        <button class="login__submit account-panel__home" type="button">Retour à l’accueil</button>
      </div>
    `);

    const find = (selector: string): HTMLElement => {
      const found = this.element.querySelector<HTMLElement>(selector);
      if (found === null) throw new Error(`Account tab markup is missing ${selector}`);
      return found;
    };
    this.error = find('.login__error');
    this.emailLine = find('.account-panel__email');
    this.list = find('.account-panel__roster');
    const form = find('.account-panel__upgrade');
    if (!(form instanceof HTMLFormElement)) throw new Error('Account tab form is not a form');
    this.form = form;
    this.providers = find('.account-panel__providers');
    this.notice = find('.account-panel__notice');

    find('.account-panel__home').addEventListener('click', () => {
      requestHome();
    });
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.upgrade();
    });
  }

  private fail(cause: unknown): void {
    this.error.textContent = uiText(
      cause instanceof AuthError ? cause.message : 'Action impossible',
    );
    this.error.hidden = false;
  }

  private upgrade(): void {
    const submit = this.form.querySelector<HTMLButtonElement>('button');
    this.error.hidden = true;
    if (submit !== null) submit.disabled = true;
    const data = new FormData(this.form);
    const email = data.get('email');
    const password = data.get('password');
    upgradeAccount(
      this.session.token,
      typeof email === 'string' ? email : '',
      typeof password === 'string' ? password : '',
    )
      .then((saved) => {
        this.emailLine.textContent = uiText(saved);
        this.form.hidden = true;
      })
      .catch((cause: unknown) => {
        this.fail(cause);
        if (submit !== null) submit.disabled = false;
      });
  }

  /** Who the account is known as: its address, else the services it signs in with. */
  private describe(email: string | null, linked: readonly SignInProvider[]): string {
    const services = linked.map((provider) => PROVIDER_NAMES[provider]).join(', ');
    if (email !== null) return services === '' ? email : `${email} (${services})`;
    return isGuestAccount({ email, providers: linked })
      ? 'Compte invité'
      : uiFormat('Compte {services}', { services });
  }

  /** One button per service offered and not linked yet; linking leaves the page. */
  private showLinks(linked: readonly SignInProvider[], offered: readonly SignInProvider[]): void {
    const missing = SIGN_IN_PROVIDERS.filter(
      (provider) => offered.includes(provider) && !linked.includes(provider),
    );
    this.providers.replaceChildren(
      ...missing.map((provider) => {
        const link = providerButton(
          provider,
          uiFormat('Lier {provider}', { provider: PROVIDER_NAMES[provider] }),
          'login__submit',
        );
        link.addEventListener('click', () => {
          this.error.hidden = true;
          link.disabled = true;
          leaveForProvider(provider, this.session.token).catch((cause: unknown) => {
            link.disabled = false;
            this.fail(cause);
          });
        });
        return link;
      }),
    );
    const block = this.providers.parentElement;
    if (block !== null) block.hidden = missing.length === 0;
  }

  /** The roster is the server's; it is fetched the first time the tab shows. */
  async refresh(): Promise<void> {
    const notice = takeLinkNotice();
    if (notice !== null) {
      const target = notice.tone === 'ok' ? this.notice : this.error;
      target.textContent = uiText(notice.text);
      target.hidden = false;
    }
    if (this.loaded) return;
    try {
      const [roster, atlas, offered] = await Promise.all([
        listRoster(this.session.token),
        loadCharacterAtlas(),
        signInProviders(),
      ]);
      this.loaded = true;
      this.emailLine.textContent = uiText(this.describe(roster.email, roster.providers));
      this.form.hidden = roster.email !== null;
      this.showLinks(roster.providers, offered);
      this.list.replaceChildren();
      for (const character of roster.characters) {
        const current = character.id === this.session.character.id;
        const card = characterCard(character, atlas, current, () => {
          this.remove(character, card);
        });
        if (!current) {
          const swap = (): void => {
            this.switchTo(character).catch((cause: unknown) => {
              this.fail(cause);
            });
          };
          card.addEventListener('click', swap);
          card.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' || event.key === ' ') swap();
          });
        }
        this.list.append(card);
      }
    } catch (cause: unknown) {
      this.fail(cause);
    }
  }

  private remove(character: CharacterSummary, card: HTMLElement): void {
    this.error.hidden = true;
    confirmCharacterDeletion(character, (name) =>
      deleteCharacter(this.session.token, character.id, name),
    )
      .then((deleted) => {
        if (deleted) card.remove();
      })
      .catch((cause: unknown) => {
        this.fail(cause);
      });
  }

  /** A new character session, then a fresh page: the zone room is joined from scratch. */
  private async switchTo(character: CharacterSummary): Promise<void> {
    await selectCharacter(this.session.token, character.id);
    window.location.reload();
  }
}
